import { PlanSchema, changePlanFormat, createPlan, type Format, type Plan } from "@roxpacer/core";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export const STORAGE_KEY = "roxpacer:plans";

interface PlansState {
  plans: Plan[];
  activeId: string;
  /** Ids of deleted plans not yet deleted from the account. */
  deletedIds: string[];
  newPlan: (format: Format) => void;
  selectPlan: (id: string) => void;
  /** Applies a change to the active plan; ignored if the result is not a valid plan. */
  updateActive: (update: (plan: Plan) => Plan) => void;
  setFormat: (format: Format) => void;
  duplicateActive: () => void;
  deleteActive: () => void;
  /** Adds an imported plan, giving it a new id if one with the same id exists. */
  importPlan: (plan: Plan) => void;
  /** Replaces all plans (e.g. after syncing), keeping the active plan if it still exists. */
  replacePlans: (plans: Plan[]) => void;
  /** Marks deletions as done in the account. */
  clearDeleted: (ids: string[]) => void;
  /** Starts over with a single new plan (e.g. on sign-out). */
  reset: () => void;
}

function initialState(): Pick<PlansState, "plans" | "activeId" | "deletedIds"> {
  const plan = createPlan({ format: "singles" });
  return { plans: [plan], activeId: plan.id, deletedIds: [] };
}

function touch(plan: Plan): Plan {
  return { ...plan, updatedAt: new Date().toISOString() };
}

export const usePlans = create<PlansState>()(
  persist(
    (set, get) => {
      const replaceActive = (next: Plan) =>
        set((s) => ({ plans: s.plans.map((p) => (p.id === s.activeId ? next : p)) }));
      const add = (plan: Plan) => set((s) => ({ plans: [...s.plans, plan], activeId: plan.id }));

      return {
        ...initialState(),
        newPlan: (format) => add(createPlan({ format, name: `New ${format} plan` })),
        selectPlan: (id) => {
          if (get().plans.some((p) => p.id === id)) set({ activeId: id });
        },
        updateActive: (update) => {
          const next = touch(update(selectActivePlan(get())));
          if (PlanSchema.safeParse(next).success) replaceActive(next);
        },
        setFormat: (format) =>
          replaceActive(touch(changePlanFormat(selectActivePlan(get()), format))),
        duplicateActive: () => {
          const source = selectActivePlan(get());
          const now = new Date().toISOString();
          add({
            ...structuredClone(source),
            id: crypto.randomUUID(),
            name: `${source.name} (copy)`.slice(0, 100),
            createdAt: now,
            // Later than createdAt, so the copy doesn't count as an untouched plan.
            updatedAt: new Date(Date.parse(now) + 1).toISOString(),
          });
        },
        deleteActive: () => {
          const { plans, activeId, deletedIds } = get();
          const remaining = plans.filter((p) => p.id !== activeId);
          const deleted = [...deletedIds, activeId];
          if (remaining.length === 0) {
            set({ ...initialState(), deletedIds: deleted });
          } else {
            const index = plans.findIndex((p) => p.id === activeId);
            set({
              plans: remaining,
              activeId: remaining[Math.max(0, index - 1)]!.id,
              deletedIds: deleted,
            });
          }
        },
        importPlan: (plan) => {
          const taken = get().plans.some((p) => p.id === plan.id);
          add(touch(taken ? { ...plan, id: crypto.randomUUID() } : plan));
        },
        replacePlans: (plans) => {
          if (plans.length === 0) {
            set({ ...initialState(), deletedIds: get().deletedIds });
            return;
          }
          const { activeId } = get();
          set({
            plans,
            activeId: plans.some((p) => p.id === activeId) ? activeId : plans[0]!.id,
          });
        },
        clearDeleted: (ids) =>
          set((s) => ({ deletedIds: s.deletedIds.filter((id) => !ids.includes(id)) })),
        reset: () => set(initialState()),
      };
    },
    {
      name: STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ plans: s.plans, activeId: s.activeId, deletedIds: s.deletedIds }),
      // Stored data is untrusted: keep only plans that still validate.
      merge: (persisted, current) => {
        const stored = (persisted ?? {}) as {
          plans?: unknown;
          activeId?: unknown;
          deletedIds?: unknown;
        };
        const deletedIds = Array.isArray(stored.deletedIds)
          ? stored.deletedIds.filter((id): id is string => typeof id === "string")
          : [];
        const plans = (Array.isArray(stored.plans) ? stored.plans : []).flatMap((raw) => {
          const result = PlanSchema.safeParse(raw);
          return result.success ? [result.data] : [];
        });
        if (plans.length === 0) return { ...current, deletedIds };
        const activeId = plans.some((p) => p.id === stored.activeId)
          ? (stored.activeId as string)
          : plans[0]!.id;
        return { ...current, plans, activeId, deletedIds };
      },
    },
  ),
);

export function selectActivePlan(state: Pick<PlansState, "plans" | "activeId">): Plan {
  return state.plans.find((p) => p.id === state.activeId) ?? state.plans[0]!;
}

export function useActivePlan(): Plan {
  return usePlans(selectActivePlan);
}
